import { useSuspenseQuery } from '@apollo/client'
import { Ellipsis, LogOut, UserRound } from 'lucide-react'
import { Suspense, use } from 'react'
import { AuthDispatchContext } from '@/components/auth/context'
import { USER_NAV_QUERY } from '@/components/layout/side-nav/user-nav.queries'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/sv/sidebar'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { useCompanyPath } from '@/lib/hooks'
import type {
  UserNavQuery,
  UserNavQueryVariables,
} from '@/components/layout/side-nav/user-nav.queries.types.gen'

export const UserNavSkeleton = () => {
  return (
    <div className="p-2 flex items-center space-x-2">
      <Skeleton className="size-8 rounded-lg" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-[125px]" />
        <Skeleton className="h-2 w-[100px]" />
      </div>
    </div>
  )
}

const UserNavData = () => {
  const { logout } = use(AuthDispatchContext) ?? {}
  const { navigateToPath } = useCompanyPath()
  const { isMobile } = useSidebar()
  const { data } = useSuspenseQuery<UserNavQuery, UserNavQueryVariables>(
    USER_NAV_QUERY
  )

  const user = data?.user

  const userInitials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage
                  src={user?.image ?? ''}
                  alt={user?.fullName ?? ''}
                />
                <AvatarFallback className="rounded-lg">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user?.fullName}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {user?.email}
                </span>
              </div>
              <Ellipsis className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'top' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage
                    src={user?.image ?? ''}
                    alt={user?.fullName ?? ''}
                  />
                  <AvatarFallback className="rounded-lg">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user?.fullName}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {user?.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => navigateToPath({ pathname: '/my-account' })}
                className="cursor-pointer"
              >
                <UserRound />
                Account
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => logout?.()}
              className="cursor-pointer"
            >
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

export const UserNav = () => {
  return (
    <Suspense fallback={<UserNavSkeleton />}>
      <UserNavData />
    </Suspense>
  )
}
